import type { FC } from 'react';
import React from 'react';
import cx from 'classnames';
import { Hyperlink } from '@THC/components/Hyperlink';

import { VideoPoster } from 'Components/StaticPages/ProductPageComponents/VideoPoster/VideoPoster';

import { resolveTransportPricing } from '../../../pages/Pricing/TransportPricing.const';
import { MOTORWAY_MOVE_VIDEO_ID } from '../DiscoverMWMove.consts';

import { useVideoModal } from './VideoModal/useVideoModal';
import { VideoModal } from './VideoModal/VideoModal';
import { TestimonialsCarousel } from './TestimonialsCarousel';

import styles from './FAQSection.module.scss';

export type FAQSectionProps = {
	videoId?: string | null;
};

export const FAQSection: FC<FAQSectionProps> = ({ videoId = MOTORWAY_MOVE_VIDEO_ID }) => {
	const { closeModal, openModal: handleOpenModal, state: isVideoModalOpen } = useVideoModal();
	const pricing = resolveTransportPricing();
	const driven = pricing.find((p) => p.transportType === 'Driven')!;

	const shouldShowVideo = Boolean(videoId);

	const transportContent = (
		<>
			<h3>Transport built for business</h3>
			<p>
				Get stock delivered from anywhere in the UK to your forecourt, fast. We handle the logistics, appraisals and
				admin. You can do what you do best.
			</p>
		</>
	);

	return (
		<>
			<section className={styles.faq}>
				<div className={styles.col}>
					{shouldShowVideo ? (
						<div className={cx(styles.faqItem, styles.faqItemVideo)}>
							<VideoPoster
								alt="Play Motorway Move introduction video"
								onClick={handleOpenModal}
								youtubeVideoId={videoId!}
							/>
							<div className={styles.text}>{transportContent}</div>
						</div>
					) : (
						<div className={cx(styles.faqItem, styles.text)}>{transportContent}</div>
					)}

					<TestimonialsCarousel className={cx(styles.faqItem, styles.testimonials)} />
				</div>

				<div className={styles.col}>
					<div className={cx(styles.faqItem, styles.text)}>
						<h3>So, how much does it cost?</h3>

						<p>
							Motorway Move is £{driven.fixedCost} for the first 50 miles, plus £{driven.perMileCost} per mile after
							that. No hidden fees, full coverage, total peace of mind.
						</p>

						<p>This all-inclusive fee covers:</p>
						<ul>
							<li>Collection & delivery</li>
							<li>Comprehensive vehicle appraisal</li>
							<li>All fuel & toll costs</li>
							<li>75 minutes of on-site time</li>
							<li>Breakdown recovery</li>
							<li>50% coverage if the seller doesn’t proceed, or should you not, in the event of additional damage</li>
						</ul>

						<p>
							Want more pricing details?{' '}
							<Hyperlink as="a" href="https://pro.motorway.co.uk/pricing" label="Click here" />.
						</p>
					</div>

					<div className={cx(styles.faqItem, styles.text)}>
						<h3>Need help?</h3>
						<p>
							You’re in good hands with our Move team – logistics pros who take full liability from collection to
							forecourt.
						</p>
						<p>
							Reach out any time at{' '}
							<Hyperlink as="a" href="mailto:dealersupport@motorway.co.uk" label="dealersupport@motorway.co.uk" /> or
							talk with us on <Hyperlink as="a" href="tel:0203 988 4396" label="0203 988 4396" /> during working hours.
						</p>
					</div>
				</div>
			</section>

			{isVideoModalOpen && shouldShowVideo && <VideoModal dismissModal={closeModal} youtubeVideoId={videoId!} />}
		</>
	);
};
